import { AppBadge, AppCard, AppIconButton, AppMemberDisplay, type BadgeColor } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Pencil, Trash2 } from 'lucide-react'
import { INQUIRY_ASSIGN_STATUS_COLOR_MAP, WORK_LOCATION_LABELS } from 'shared'
import { formatCurrency } from '@/lib/format-utils'
import type { InquiryAssignLocal } from '../types'

type AssignCardProps = {
  assign: InquiryAssignLocal
  canEdit?: boolean
  onEdit?: (assign: InquiryAssignLocal) => void
  onDelete?: (id: number) => void
}

export function AssignCard({ assign, canEdit = true, onEdit, onDelete }: AssignCardProps) {
  const selectedWorkLocations = (Object.keys(WORK_LOCATION_LABELS) as (keyof typeof WORK_LOCATION_LABELS)[]).filter(
    (key) => assign.work_locations[key],
  )

  const statusColor = assign.inquiry_assign_status_name
    ? ((INQUIRY_ASSIGN_STATUS_COLOR_MAP[
        assign.inquiry_assign_status_name as keyof typeof INQUIRY_ASSIGN_STATUS_COLOR_MAP
      ] ?? 'secondary') as BadgeColor)
    : 'secondary'

  return (
    <AppCard>
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <AppBadge color={statusColor}>{assign.inquiry_assign_status_name ?? '-'}</AppBadge>
            <span className='text-sm font-medium'>{assign.role_name ?? '-'}</span>
          </div>
          <div className='flex gap-1'>
            <AppIconButton
              description='編集'
              size='sm'
              className='hover:bg-muted'
              disabled={!canEdit}
              onClick={() => onEdit?.(assign)}
            >
              <Pencil className='size-3.5' />
            </AppIconButton>
            <AppIconButton
              description='削除'
              size='sm'
              className='hover:bg-muted'
              disabled={!canEdit}
              onClick={() => onDelete?.(assign.id)}
            >
              <Trash2 className='size-3.5' />
            </AppIconButton>
          </div>
        </div>
        <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-sm md:grid-cols-3'>
          <div>
            <span className='text-muted-foreground'>メンバー</span>
            <AppMemberDisplay
              employeeNumber={assign.employee_number}
              name={assign.member_name ?? 'TBA'}
              emptyDisplay='dash'
            />
          </div>
          <div>
            <span className='text-muted-foreground'>雇用形態</span>
            <div>{assign.employee_type_name ?? '-'}</div>
          </div>
          <div>
            <span className='text-muted-foreground'>作業場所</span>
            <div className='flex flex-wrap gap-1'>
              {selectedWorkLocations.length > 0
                ? selectedWorkLocations.map((key) => (
                    <AppBadge key={key} color='secondary'>
                      {WORK_LOCATION_LABELS[key]}
                    </AppBadge>
                  ))
                : '-'}
            </div>
          </div>
          <div>
            <span className='text-muted-foreground'>単価</span>
            <div>{formatCurrency(assign.unit_price)}</div>
          </div>
          <div>
            <span className='text-muted-foreground'>人月</span>
            <div>{assign.assign_person_month.toFixed(2)}</div>
          </div>
          <div>
            <span className='text-muted-foreground'>参画予定</span>
            <div>{assign.assigned_on ?? '-'}</div>
          </div>
        </div>
        {assign.notes && <p className='text-sm text-muted-foreground'>{assign.notes}</p>}
      </div>
    </AppCard>
  )
}
